import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Chip,
  Box,
  Rating,
  TextField,
  IconButton,
  Collapse,
  Divider,
  Tooltip,
  Paper,
  alpha
} from '@mui/material';
import FlavorImage from './FlavorImage';
import {
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  LocalFireDepartment as FireIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { Flavor } from '../../utils/filterUtils';

// Styled expand button with animation
const ExpandButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'expanded',
})<{ expanded: boolean }>(({ theme, expanded }) => ({
  transform: !expanded ? 'rotate(0deg)' : 'rotate(180deg)',
  marginLeft: 'auto',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));

// Styled chip with hover effect
const StyledChip = styled(Chip)(({ theme }) => ({
  borderRadius: '8px',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
  },
  '&.MuiChip-filled': {
    background: theme.palette.mode === 'dark'
      ? `linear-gradient(45deg, ${alpha(theme.palette.primary.main, 0.8)} 30%, ${alpha(theme.palette.secondary.main, 0.8)} 90%)`
      : `linear-gradient(45deg, ${alpha(theme.palette.primary.main, 0.2)} 30%, ${alpha(theme.palette.secondary.main, 0.2)} 90%)`,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
  },
}));

// Styled rating with custom stars
const StyledRating = styled(Rating)(({ theme }) => ({
  '& .MuiRating-iconFilled': {
    color: theme.palette.secondary.main,
  },
  '& .MuiRating-iconHover': {
    color: theme.palette.secondary.light,
  },
}));

// Animated fire icon for intensity
const IntensityIcon = styled(FireIcon, {
  shouldForwardProp: (prop) => prop !== 'intensity',
})<{ intensity: number }>(({ theme, intensity }) => ({
  color: intensity > 3 
    ? theme.palette.error.main 
    : intensity > 1 
      ? theme.palette.warning.main 
      : theme.palette.success.main,
  fontSize: `${14 + intensity * 2}px`,
  animation: intensity > 3 ? 'flame 1.5s infinite' : 'none',
  '@keyframes flame': {
    '0%': { opacity: 0.8, transform: 'scale(0.95)' },
    '50%': { opacity: 1, transform: 'scale(1.05)' },
    '100%': { opacity: 0.8, transform: 'scale(0.95)' },
  },
}));

interface FlavorCardProps {
  flavor: Flavor;
  isFavorite: boolean;
  hasTried: boolean;
  userRating: number | null;
  userNote: string | null;
  onAddToFavorites: (flavorId: string) => void;
  onRemoveFromFavorites: (flavorId: string) => void;
  onMarkAsTried: (flavorId: string) => void;
  onRateFlavor: (flavorId: string, rating: number) => void;
  onSaveNote: (flavorId: string, note: string) => void;
}

const FlavorCard: React.FC<FlavorCardProps> = ({
  flavor,
  isFavorite,
  hasTried,
  userRating,
  userNote,
  onAddToFavorites,
  onRemoveFromFavorites,
  onMarkAsTried,
  onRateFlavor,
  onSaveNote
}) => {
  const [expanded, setExpanded] = useState(false);
  const [note, setNote] = useState(userNote || '');
  const [isHovered, setIsHovered] = useState(false);

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const handleFavoriteClick = () => {
    if (isFavorite) {
      onRemoveFromFavorites(flavor.id);
    } else {
      onAddToFavorites(flavor.id);
    }
  };

  const handleTriedClick = () => {
    if (!hasTried) {
      onMarkAsTried(flavor.id);
    }
  };

  const handleRatingChange = (_event: React.SyntheticEvent, newValue: number | null) => {
    if (newValue !== null) {
      onRateFlavor(flavor.id, newValue);
    }
  };

  const handleNoteChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNote(event.target.value);
  };

  const handleSaveNote = () => {
    onSaveNote(flavor.id, note);
  };

  // Get number of fire icons based on intensity level
  const getFireIconCount = () => {
    switch (flavor.intensity) {
      case 'mild':
        return 1;
      case 'medium':
        return 3;
      case 'strong':
        return 5;
      default:
        return 1; // Default to mild if not specified
    }
  };

  const fireIconCount = getFireIconCount();
  
  // Check if flavor is new (released in the last 30 days)
  const isNewRelease = flavor.releaseDate ? 
    (new Date().getTime() - new Date(flavor.releaseDate).getTime()) < (30 * 24 * 60 * 60 * 1000) : 
    false;

  return (
    <Card 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        transition: 'all 0.3s ease',
        transform: isHovered ? 'translateY(-8px)' : 'none',
        boxShadow: isHovered 
          ? '0 12px 28px rgba(0, 0, 0, 0.15)' 
          : '0 8px 16px rgba(0, 0, 0, 0.1)',
        borderRadius: '16px',
        overflow: 'hidden',
        position: 'relative',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* New Release Badge */}
      {isNewRelease && (
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 10,
            background: 'linear-gradient(45deg, #FF3D71 30%, #FF8A00 90%)',
            color: 'white',
            padding: '4px 8px',
            borderRadius: '8px',
            fontWeight: 'bold',
            fontSize: '0.75rem',
            boxShadow: '0 2px 8px rgba(255, 61, 113, 0.5)',
            transform: 'rotate(5deg)',
          }}
        >
          NEW
        </Box>
      )}

      <FlavorImage 
        flavorName={flavor.name} 
        edition={flavor.edition} 
        productType={flavor.productType}
        height={200} 
        imagePath={flavor.imagePath}
      />
      
      <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
        <Typography 
          gutterBottom 
          variant="h5" 
          component="div"
          sx={{ 
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          {flavor.name}
          {fireIconCount > 0 && (
            <Tooltip title={`Intensity: ${flavor.intensity || 'mild'}`}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {[...Array(fireIconCount)].map((_, i) => (
                  <IntensityIcon key={i} intensity={fireIconCount > 4 ? 5 : fireIconCount > 2 ? 3 : 1} />
                ))}
              </Box>
            </Tooltip>
          )}
        </Typography>
        
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ 
            mb: 2,
            lineHeight: 1.6,
          }}
        >
          {flavor.description}
        </Typography>
        
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            mb: 2,
            p: 1,
            borderRadius: '8px',
            bgcolor: (theme) => alpha(theme.palette.background.paper, 0.5),
          }}
        >
          <Tooltip title="Nicotine Strength">
            <Typography 
              variant="body2"
              sx={{ 
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {flavor.nicotineStrength}mg
            </Typography>
          </Tooltip>
          
          <Tooltip title="Puff Count">
            <Typography 
              variant="body2"
              sx={{ 
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {flavor.puffCount} puffs
            </Typography>
          </Tooltip>
        </Box>
        
        <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {flavor.categories.map((category) => (
            <StyledChip
              key={category}
              label={category}
              size="small"
              variant="filled"
            />
          ))}
        </Box>
        
        <Divider sx={{ my: 1.5 }} />
        
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography variant="body2" sx={{ mr: 1, fontWeight: 500 }}>
            Rating:
          </Typography>
          <StyledRating
            value={flavor.averageRating}
            precision={0.5}
            readOnly
            size="small"
          />
          <Typography variant="body2" sx={{ ml: 1, fontWeight: 700 }}>
            ({flavor.averageRating.toFixed(1)})
          </Typography>
        </Box>
        
        {hasTried && (
          <Paper 
            elevation={0} 
            sx={{ 
              mt: 2, 
              p: 1.5, 
              borderRadius: '8px',
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
              border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            }}
          >
            <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, display: 'flex', alignItems: 'center' }}>
              <CheckCircleIcon fontSize="small" sx={{ mr: 0.5, color: 'success.main' }} />
              Your Rating:
            </Typography>
            <StyledRating
              value={userRating}
              onChange={handleRatingChange}
              size="medium"
            />
          </Paper>
        )}
      </CardContent>
      
      {/* Buy Now Button */}
      <Box sx={{ px: 2, pb: 1 }}>
        <Button
          fullWidth
          variant="contained"
          color="secondary"
          component="a"
          href="https://www.vaporfi.com/catalogsearch/result/?q=geek+bar"
          target="_blank"
          rel="nofollow noopener noreferrer"
          sx={{
            borderRadius: '8px',
            py: 1,
            fontWeight: 'bold',
            boxShadow: '0 4px 8px rgba(255, 61, 113, 0.3)',
            background: 'linear-gradient(45deg, #FF3D71 30%, #FF8A00 90%)',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 12px rgba(255, 61, 113, 0.4)',
            },
          }}
        >
          Buy Now
        </Button>
      </Box>
      
      <CardActions 
        disableSpacing 
        sx={{ 
          p: 2, 
          pt: 0,
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Tooltip title={isFavorite ? "Remove from favorites" : "Add to favorites"}>
          <IconButton
            aria-label={isFavorite ? 'remove from favorites' : 'add to favorites'}
            onClick={handleFavoriteClick}
            color={isFavorite ? 'secondary' : 'default'}
            sx={{ 
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'scale(1.1)',
              },
            }}
          >
            {isFavorite ? <FavoriteIcon /> : <FavoriteBorderIcon />}
          </IconButton>
        </Tooltip>
        
        {hasTried ? (
          <Chip
            icon={<CheckCircleIcon fontSize="small" />}
            label="Tried"
            size="small"
            color="primary"
            variant="outlined"
            sx={{ 
              borderRadius: '16px',
              fontWeight: 500,
            }}
          />
        ) : (
          <Typography
            variant="body2"
            onClick={handleTriedClick}
            sx={{ 
              display: 'flex',
              alignItems: 'center',
              color: 'primary.main',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                textDecoration: 'underline',
                transform: 'translateY(-1px)',
              },
            }}
          >
            <Box component="span" sx={{ mr: 0.5, display: 'flex', alignItems: 'center' }}>
              <CheckCircleIcon fontSize="small" />
            </Box>
            Mark as Tried
          </Typography>
        )}
        
        {hasTried && (
          <Tooltip title={expanded ? "Hide notes" : "Show notes"}>
            <ExpandButton
              expanded={expanded}
              onClick={handleExpandClick}
              aria-expanded={expanded}
              aria-label="show notes"
            >
              <ExpandMoreIcon />
            </ExpandButton>
          </Tooltip>
        )}
      </CardActions>
      
      {hasTried && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <CardContent sx={{ pt: 0 }}>
            <Typography 
              variant="body2" 
              paragraph 
              sx={{ 
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <EditIcon fontSize="small" sx={{ mr: 0.5 }} />
              Your Notes:
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              placeholder="Add your notes about this flavor..."
              value={note}
              onChange={handleNoteChange}
              sx={{ 
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                },
              }}
            />
            <Button
              variant="contained"
              size="small"
              onClick={handleSaveNote}
              color="primary"
              sx={{ 
                borderRadius: '8px',
                boxShadow: '0 4px 8px rgba(0, 191, 255, 0.2)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 12px rgba(0, 191, 255, 0.3)',
                },
              }}
            >
              Save Note
            </Button>
          </CardContent>
        </Collapse>
      )}
    </Card>
  );
};

export default FlavorCard;
